export type AppointmentFinancialStatus = "pendente" | "pago" | "nao_cobrado";

export type AppointmentFinancialSource = "avulso" | "pacote" | "misto" | "manual";

export type AppointmentFinancialRow = {
  appointment_id: string;
  status: AppointmentFinancialStatus;
  source: AppointmentFinancialSource;
  owner_package_id: string | null;
  sale_id: string | null;
  is_covered_by_package: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

const normalizePaidValue = (status?: string | null): AppointmentFinancialStatus | null => {
  if (!status) return null;

  if (status === "pago") {
    return "pago";
  }

  if (status === "pendente" || status === "nao_cobrado") {
    return status;
  }

  return null;
};

export const isLegacyPaidAppointment = (appointment: {
  status?: string | null;
  grooming_status?: string | null;
}): boolean => appointment.status === "pago" || appointment.grooming_status === "pago";

export const deriveFinancialStatusWithLegacyFallback = (
  financial:
    | {
        status?: string | null;
      }
    | null
    | undefined,
  appointment: { status?: string | null; grooming_status?: string | null },
  sale?:
    | {
        status?: string | null;
        sale_payments?: ArrayLike<{
          method?: string | null;
          amount?: number | string | null;
          received_at?: string | null;
        }> | null;
      }
    | null
    | undefined,
): AppointmentFinancialStatus => {
  const hasCancelledSale =
    Boolean(sale?.status) &&
    (sale?.status === "CANCELADA" || sale?.status === "DEVOLVIDA");
  const isActiveSale = Boolean(sale) && !hasCancelledSale;

  // Active sale fully paid → settled.
  if (isActiveSale && !hasOutstandingSaleReceivable(sale!)) {
    return "pago";
  }

  const status = normalizePaidValue(financial?.status);

  // If the linked sale was cancelled, the "pago" stored in appointment_financial is stale —
  // ignore it so the UI doesn't show "Pago" after a cancellation.
  if (status === "pago" && hasCancelledSale) {
    return "pendente";
  }

  if (status) {
    return status;
  }

  if (isLegacyPaidAppointment(appointment)) {
    return "pago";
  }

  return "pendente";
};

export const isFinancialSettled = (
  financial: { status?: string | null } | null | undefined,
  appointment: { status?: string | null; grooming_status?: string | null },
): boolean => deriveFinancialStatusWithLegacyFallback(financial, appointment) === "pago";

const getCollectionLength = (value: ArrayLike<unknown> | null | undefined): number => {
  if (!value || typeof value.length !== "number") return 0;
  return value.length;
};

export const hasAppointmentSale = (
  sale: { id?: string | null } | null | undefined,
): boolean => Boolean(sale?.id);

export const getOutstandingSaleReceivableAmount = (
  sale:
    | {
        sale_payments?: ArrayLike<{
          method?: string | null;
          amount?: number | string | null;
          received_at?: string | null;
        }> | null;
      }
    | null
    | undefined,
): number => {
  const payments = sale?.sale_payments;
  if (!payments) return 0;

  let total = 0;
  for (let index = 0; index < payments.length; index += 1) {
    const payment = payments[index];
    if (!payment || payment.method !== "A_RECEBER" || payment.received_at) continue;
    total += Number(payment.amount ?? 0) || 0;
  }

  return total;
};

export const hasOutstandingSaleReceivable = (
  sale:
    | {
        sale_payments?: ArrayLike<{
          method?: string | null;
          amount?: number | string | null;
          received_at?: string | null;
        }> | null;
      }
    | null
    | undefined,
): boolean => getOutstandingSaleReceivableAmount(sale) > 0;

export const hasBillableAppointmentContent = ({
  appointmentItems,
  packageCharges,
}: {
  appointmentItems?: ArrayLike<{
    qty?: number | string | null;
    item?: { sale_price?: number | string | null } | null;
  }> | null;
  packageCharges?: ArrayLike<{
    amount?: number | string | null;
    suggestedChargeAmount?: number | string | null;
    included?: boolean | null;
  }> | null;
}): boolean => {
  const hasPositiveAppointmentItem = (() => {
    if (!appointmentItems) return false;

    for (let index = 0; index < appointmentItems.length; index += 1) {
      const item = appointmentItems[index];
      if (!item) continue;

      const qty = Number(item.qty ?? 1) || 0;
      const salePrice = Number(item.item?.sale_price ?? 0) || 0;
      if (qty > 0 && salePrice > 0) return true;
    }

    return false;
  })();

  if (hasPositiveAppointmentItem) return true;

  if (!packageCharges) return false;
  for (let index = 0; index < packageCharges.length; index += 1) {
    const charge = packageCharges[index];
    if (!charge || charge.included === false) continue;
    const chargeAmount = Number(charge.amount ?? charge.suggestedChargeAmount ?? 0) || 0;
    if (chargeAmount > 0) return true;
  }

  return false;
};

export const canOpenAppointmentPayment = ({
  sale,
  appointmentItems,
  packageCharges,
  outstandingBalance,
}: {
  sale?: { id?: string | null; sale_payments?: Parameters<typeof hasOutstandingSaleReceivable>[0]["sale_payments"] } | null;
  appointmentItems?: ArrayLike<unknown> | null;
  packageCharges?: ArrayLike<unknown> | null;
  outstandingBalance?: number | null;
}): boolean => {
  if (hasAppointmentSale(sale)) {
    return hasOutstandingSaleReceivable(sale);
  }

  if (typeof outstandingBalance === "number") {
    return outstandingBalance > 0;
  }

  return hasBillableAppointmentContent({ appointmentItems, packageCharges });
};

export const deriveAppointmentFinancialDraftState = ({
  hasAppointmentItems,
  packageCount,
  hasOutstandingPackages,
}: {
  hasAppointmentItems: boolean;
  packageCount: number;
  hasOutstandingPackages: boolean;
}): {
  status: AppointmentFinancialStatus;
  source: AppointmentFinancialSource;
} => {
  const hasPackages = packageCount > 0;

  const status: AppointmentFinancialStatus =
    hasPackages && !hasOutstandingPackages && !hasAppointmentItems
      ? "pago"
      : "pendente";

  const source: AppointmentFinancialSource =
    hasPackages && hasAppointmentItems
      ? "misto"
      : hasPackages
        ? "pacote"
        : hasAppointmentItems
          ? "avulso"
          : "manual";

  return { status, source };
};
